//
// # AddExprLabelDialog
//
// Modal dialog for configuring a new expression label before placement.
// Fields: Kind (from ExprKind), plus kind-specific fields (e.g. port name).
//

import React, { useState, useCallback } from "react";
import { Modal } from "../Modal/Modal";
import { schema } from "GenAlphaSchema";

// User-facing expression kinds, derived from the schema.
// Filter out "invalid" and "port-debug" which aren't user-creatable.
const HIDDEN_KINDS = new Set<string>(["invalid", "port-debug"]);
export const EXPR_KIND_OPTIONS = Object.entries(schema.symbol.ExprKind)
  .filter(([, value]) => !HIDDEN_KINDS.has(value))
  .map(([label, value]) => ({ value, label }));

// Initial editable values shown in the dialog's name inputs when no current
// value is supplied (Add flow; or Edit flow when switching to a kind whose
// field wasn't populated on the original label).
export const DEFAULT_PORT_NAME = "port0";
export const DEFAULT_PARAM_NAME = "param0";

// The result returned when the user confirms the dialog.
export interface ExprLabelConfig {
  readonly kind: schema.symbol.ExprKind;
  readonly name?: string; // For port-name kind
  readonly paramName?: string; // For inst-param kind
}

interface AddExprLabelDialogProps {
  readonly onConfirm: (config: ExprLabelConfig) => void;
  readonly onCancel: () => void;
}

export function AddExprLabelDialog({
  onConfirm,
  onCancel,
}: AddExprLabelDialogProps) {
  const [kind, setKind] = useState<schema.symbol.ExprKind>("cell-name");
  const [name, setName] = useState(DEFAULT_PORT_NAME);
  const [paramName, setParamName] = useState(DEFAULT_PARAM_NAME);

  // Only port-name and inst-param have a free-text field that needs validation;
  // other kinds derive their text from the model and have nothing to validate.
  const trimmedName = name.trim();
  const trimmedParamName = paramName.trim();
  const canSubmit =
    kind === "port-name"
      ? trimmedName.length > 0
      : kind === "inst-param"
      ? trimmedParamName.length > 0
      : true;

  const handleConfirm = useCallback(() => {
    if (!canSubmit) return;
    const config: ExprLabelConfig = { kind };
    if (kind === "port-name") {
      onConfirm({ ...config, name: trimmedName });
    } else if (kind === "inst-param") {
      onConfirm({ ...config, paramName: trimmedParamName });
    } else {
      onConfirm(config);
    }
  }, [canSubmit, kind, trimmedName, trimmedParamName, onConfirm]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && canSubmit) {
        e.preventDefault();
        handleConfirm();
      }
    },
    [canSubmit, handleConfirm]
  );

  const selectStyle = {
    backgroundColor: "var(--color-ide-surface-2)",
    borderColor: "var(--color-ide-border)",
    color: "var(--color-ide-text)",
  };

  const inputStyle = (valid: boolean) => ({
    backgroundColor: "var(--color-ide-surface-2)",
    borderColor: valid ? "var(--color-ide-border)" : "var(--color-ide-error)",
    color: "var(--color-ide-text)",
  });

  return (
    <Modal open={true} onClose={onCancel} title="Add Expression Label">
      <div onKeyDown={handleKeyDown} className="flex flex-col gap-3">
        {/* Kind */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Kind
          </label>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as schema.symbol.ExprKind)}
            autoFocus
            className="px-2 py-1.5 text-[13px] rounded border outline-none"
            style={selectStyle}
          >
            {EXPR_KIND_OPTIONS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </div>

        {/* Port Name (only for port-name kind) */}
        {kind === "port-name" && (
          <div className="flex flex-col gap-1">
            <label
              className="text-[12px]"
              style={{ color: "var(--color-ide-text-muted)" }}
            >
              Port Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="px-2 py-1.5 text-[13px] rounded border outline-none"
              style={inputStyle(trimmedName.length > 0)}
            />
          </div>
        )}

        {/* Param Name (only for inst-param kind) */}
        {kind === "inst-param" && (
          <div className="flex flex-col gap-1">
            <label
              className="text-[12px]"
              style={{ color: "var(--color-ide-text-muted)" }}
            >
              Parameter Name
            </label>
            <input
              type="text"
              value={paramName}
              onChange={(e) => setParamName(e.target.value)}
              className="px-2 py-1.5 text-[13px] rounded border outline-none"
              style={inputStyle(trimmedParamName.length > 0)}
            />
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={onCancel}
            className="px-4 py-1.5 text-[13px] rounded border cursor-pointer bg-transparent"
            style={{
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canSubmit}
            className="px-4 py-1.5 text-[13px] rounded border-none"
            style={{
              backgroundColor: canSubmit
                ? "var(--color-ide-accent)"
                : "var(--color-ide-surface-2)",
              color: canSubmit ? "#ffffff" : "var(--color-ide-text-dim)",
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            Add Label
          </button>
        </div>
      </div>
    </Modal>
  );
}
